import * as React from "react";
import { withRouter } from "react-router-dom";

import { Dispatch } from "redux";
import LockOpenIcon from "@material-ui/icons/LockOpen";

import { setActiveDialog } from "src/store/adminHtml/actions";
import { connect } from "react-redux";
import { AppState } from "src/store";
import { navigation } from "src/config/navigation";

import LOGO from "src/assets/svgs/logo.png";
import EMBLEM_LOGO from "src/assets/svgs/Emblem_Logo.svg";

import {
  Button,
  Drawer,
  DrawerProps,
  withStyles,
  WithStyles,
  createStyles,
  Divider,
  lighten,
  darken,
} from "@material-ui/core";
import Box from "@material-ui/core/Box";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Tooltip from "@material-ui/core/Tooltip";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

import {
  userPlanSelector,
  userTrialEndDateSelector,
} from "src/store/system/selector";

import type { ActiveDialog } from "src/interfaces/adminHtmlState.interface";

import NavigatorItem from "./NavigatorItem";

import type { Theme } from "src/theme";
import SubscriptionDetails from "./SubscriptionDetails";
import { recordEventWithCheck } from "src/apiService/modules/events";

export const navigatorStyles = (theme: Theme) => {
  const { custom: palette, derived } = theme.palette.custom;
  const cardColor = palette.card;
  const shadowSm = derived.shadow?.sm || "2px 0 4px rgba(0,0,0,0.1)";
  const isDark = theme.palette.type === "dark";

  // Use green for upgrade button - stands out as a call-to-action
  const upgradeButtonBg = isDark
    ? darken(palette.brandSecondary, 0.15)
    : palette.brandSecondary;
  const upgradeButtonHoverBg = isDark
    ? palette.brandSecondary
    : lighten(palette.brandSecondary, 0.2);
  const upgradeIconColor = isDark
    ? lighten(palette.brandSecondary, 0.2)
    : palette.brandSecondary;

  return createStyles({
    list: {
      display: "flex",
      flexDirection: "column",
      flex: 1,
    },
    item: {
      backgroundColor: cardColor,
      paddingTop: 15,
      paddingBottom: 15,
      justifyContent: "center",
    },
    logo: {
      filter: isDark ? "brightness(0) invert(1)" : "none",
    },
    emblemLogo: {
      height: 48,
      width: 48,
    },
    fullLogo: {
      height: theme.spacing(12), // 96px, equivalent to h-24
    },
    expander: {
      flex: 1,
    },
    upgrade: {
      alignSelf: "center",
      backgroundColor: upgradeButtonBg,
      color: theme.palette.common.white,
      border: "none",
      "&:hover": {
        backgroundColor: upgradeButtonHoverBg,
      },
    },
    upgradeCollapsed: {
      minWidth: "auto",
      padding: theme.spacing(1),
      margin: theme.spacing(0.5, "auto"),
      width: 48,
      height: 48,
      borderRadius: "50%",
      backgroundColor: "transparent",
      color: upgradeIconColor,
      transition: "transform 0.2s ease, color 0.2s ease",
      "&:hover": {
        backgroundColor: "transparent",
        transform: "scale(1.1)",
        color: isDark
          ? lighten(palette.accent, 0.4)
          : theme.palette.primary.main,
      },
      "& svg": {
        fontSize: 28,
      },
    },
    trialEndDateNotice: {
      borderRadius: theme.shape.borderRadius,
      color: theme.palette.common.white,
      backgroundColor: theme.palette.colors.blue,
      justifyContent: "center",
    },
    collapseTab: {
      position: "absolute",
      right: -16,
      top: "50%",
      transform: "translateY(-50%)",
      width: 16,
      height: 48,
      backgroundColor: cardColor,
      borderRadius: "0 8px 8px 0",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      boxShadow: shadowSm,
      transition: "background-color 0.2s ease",
      "&:hover": {
        backgroundColor: theme.palette.colors.lightGray,
      },
      zIndex: 1,
    },
    collapseTabIcon: {
      color: derived.accentOnCard,
      fontSize: 16,
    },
  });
};

interface NavigatorOwnProps extends WithStyles<typeof navigatorStyles> {
  title: string;
  subTitle: string;
  variant?: "permanent" | "persistent" | "temporary";
  open?: boolean;
  onClickSubscriptionDetails: () => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export interface NavigatorStateProps {
  plan?: string;
  trialEndDate?: Date;
}

interface NavigatorDispatchProps {
  onItemClick: (id: string) => void;
  dispatchSetActiveDialog: (dialog: ActiveDialog) => void;
}

export type NavigatorProps = NavigatorStateProps &
  NavigatorOwnProps &
  DrawerProps &
  NavigatorDispatchProps &
  any;

class Navigator extends React.Component<NavigatorProps, any> {
  constructor(props: any) {
    super(props);

    this.state = {};
  }

  async componentDidMount() {
    const { location, onItemClick } = this.props;
    const nav = navigation.find(
      (e) => "href" in e && e.href === location.pathname
    );
    if (nav && nav.id) {
      onItemClick(nav.id);
    }
  }

  render() {
    const {
      title,
      subTitle,
      variant,
      classes,
      onItemClick,
      onClose,
      staticContext,
      plan,
      dispatchSetActiveDialog,
      trialEndDate,
      collapsed = false,
      onToggleCollapse,
      onClickSubscriptionDetails,
      ...other
    } = this.props;
    return (
      <Drawer variant={variant} {...other} onClose={() => onClose()}>
        {onToggleCollapse && (
          <Box className={classes.collapseTab} onClick={onToggleCollapse}>
            {collapsed ? (
              <ChevronRightIcon className={classes.collapseTabIcon} />
            ) : (
              <ChevronLeftIcon className={classes.collapseTabIcon} />
            )}
          </Box>
        )}
        <List disablePadding className={classes.list}>
          <ListItem className={classes.item}>
            {collapsed ? (
              <img
                src={EMBLEM_LOGO}
                alt={title}
                className={`${classes.emblemLogo} ${classes.logo}`}
              />
            ) : (
              <img
                src={LOGO}
                alt={title}
                className={`${classes.fullLogo} ${classes.logo}`}
              />
            )}
          </ListItem>
          {navigation.map((nav) => {
            if ("dialog" in nav) {
              if ("render" in nav && nav.render)
                return (
                  <React.Fragment key={nav.id}>
                    {nav.render({
                      entry: nav,
                      dispatch: dispatchSetActiveDialog,
                      collapsed,
                    })}
                  </React.Fragment>
                );
              return (
                <NavigatorItem
                  key={nav.id}
                  onClick={() => {
                    dispatchSetActiveDialog(nav.dialog);
                  }}
                  Icon={nav.Icon}
                  primary={nav.title}
                  collapsed={collapsed}
                />
              );
            }

            const active =
              "href" in nav && other.location.pathname.startsWith(nav.href);

            if ("render" in nav && nav.render)
              return (
                <React.Fragment key={nav.id}>
                  {nav.render({ active, entry: nav, collapsed })}
                </React.Fragment>
              );

            return (
              <NavigatorItem
                id={nav.id}
                key={nav.id}
                Icon={nav.Icon}
                primary={nav.title}
                to={nav.href}
                active={other.location.pathname.startsWith(nav.href)}
                collapsed={collapsed}
                onClick={() => {
                  void recordEventWithCheck({
                    type: "feature-adoption",
                    feature: "navigation-selection",
                    element: nav.id,
                  });
                }}
              />
            );
          })}
          <ListItem className={classes.expander} />
          {plan !== "ultimate" ? (
            <Tooltip title="Click here to upgrade and enhance your accounting.">
              <Button
                variant={collapsed ? "text" : "contained"}
                color="primary"
                className={
                  collapsed ? classes.upgradeCollapsed : classes.upgrade
                }
                startIcon={collapsed ? undefined : <LockOpenIcon />}
                onClick={() =>
                  this.props.dispatchSetActiveDialog("upgrade_plan")
                }
              >
                {collapsed ? <LockOpenIcon /> : "Upgrade Plan"}
              </Button>
            </Tooltip>
          ) : trialEndDate && !collapsed ? (
            <ListItem className={classes.trialEndDateNotice}>
              Trial Expires:{" "}
              {trialEndDate.toLocaleDateString("en-US", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </ListItem>
          ) : null}
          {!collapsed && (
            <>
              <Box py={2}>
                <Divider variant="middle" />
              </Box>
              <SubscriptionDetails
                onClick={() => {
                  onClickSubscriptionDetails();
                }}
              />
            </>
          )}
        </List>
      </Drawer>
    );
  }
}

const mapDispatchToProps = (
  dispatch: Dispatch,
  ownProps: NavigatorOwnProps
): NavigatorDispatchProps => {
  return {
    onItemClick: (id: string) => {
      const nav = navigation.find((e) => e.id === id);
      if (!nav) return;

      if ("href" in nav) {
      } else if ("dialog" in nav) {
        dispatch(setActiveDialog(nav.dialog));
      }
    },
    dispatchSetActiveDialog: (dialog: ActiveDialog) =>
      dispatch(setActiveDialog(dialog)),
  };
};

const mapStateToProps = (state: AppState): NavigatorStateProps => ({
  plan: userPlanSelector(state),
  trialEndDate: userTrialEndDateSelector(state),
});

export default withStyles(navigatorStyles as any)(
  connect<
    NavigatorStateProps,
    NavigatorDispatchProps,
    NavigatorOwnProps,
    AppState
  >(
    mapStateToProps,
    mapDispatchToProps
  )(withRouter(Navigator))
);
